import React, {useEffect, useState} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import {getProductById, getProductRentalPriceByProductId} from "../../utils/requestUtils";
import {useRecoilState} from "recoil";
import Config from "../../core/config";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";
import ConfirmButton from "../common/ConfirmButton";
import DeleteButton from "../common/DeleteButton";
import DecimalInput from "../common/DecimalInput";
import Loading from "../../core/Loading";
import {loadingAtom} from "../../atoms";
import {useNavigate} from "react-router-dom";
import {UserPermission} from "../../utils/utils";
import {usePermission} from "../../core/PermissionContext";
import { showAlert } from '../../core/dialog';
import { getAuthHeader, getAuthToken } from '../../utils/auth';

const apiUrl = Config.apiUrl;

const ProductManager = () => {
    const { hasUserPermission, loadingPermissions } = usePermission();
    const [, setLoading] = useRecoilState(loadingAtom);
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('id');

    const newEntity = {
        id: undefined,
        name: '',
        stock: 1,
        availableStock: 0,
        costValue: 0,
        value: 0,
        supplier: '',
        brand: '',
        recommendedAge: '',
        details: '',
        customerOrientations: '',
        maxWeight: 0,
        keywords: '',
        pieces: []
    };
    
    const [formData, setFormData] = useState(newEntity);
    const newProductRentalPrice = {
        product: {id: 0, name: ''},
        days: 7,
        price: 0
    };

    const newProductPiece = {pieceName: '', quantity: 1};
    const [productRentalPrices, setProductRentalPrices] = useState([newProductRentalPrice]);
    const [productPieces, setProductPieces] = useState([newProductPiece]);

    const [productRentalPricesToRemove, setProductRentalPricesToRemove] = useState([]);
    const [productPiecesToRemove, setProductPiecesToRemove] = useState([]);
    const [isCopying, setIsCopying] = useState(false);
    const [productImages, setProductImages] = useState([]);
    const [imageUploading, setImageUploading] = useState(false);

    const loadProductImages = async (productId) => {
        if (!productId) return;
        const res = await fetch(`${apiUrl}/api/products/${productId}/images`, {
            headers: { ...getAuthHeader() }
        });
        if (res.ok) setProductImages(await res.json());
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const MAX_MB = 2;
        if (file.size > MAX_MB * 1024 * 1024) {
            alert(`A foto deve ter no máximo ${MAX_MB} MB.`);
            e.target.value = '';
            return;
        }
        if (!formData.id) {
            alert('Salve o produto antes de adicionar fotos.');
            e.target.value = '';
            return;
        }
        setImageUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await fetch(`${apiUrl}/api/products/${formData.id}/images`, {
                method: 'POST',
                headers: { ...getAuthHeader() },
                body: fd,
            });
            if (res.ok) {
                const img = await res.json();
                setProductImages(prev => [...prev, img]);
            } else {
                let msg = `Erro ${res.status}`;
                try { const body = await res.json(); msg += ': ' + (body.erro || body.message || JSON.stringify(body)); } catch {}
                alert(msg);
            }
        } catch (err) {
            alert('Erro de conexão: ' + err.message);
        }
        setImageUploading(false);
        e.target.value = '';
    };

    const handleImageDelete = async (imageId) => {
        if (!window.confirm('Remover esta foto?')) return;
        const res = await fetch(`${apiUrl}/api/products/${formData.id}/images/${imageId}`, {
            method: 'DELETE',
            headers: { ...getAuthHeader() },
        });
        if (res.ok) setProductImages(prev => prev.filter(img => img.id !== imageId));
    };

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : type === 'file' ? e.target.files[0] : value,
        }));
    };

    useEffect(() => {
        if (productRentalPrices[productRentalPrices.length - 1].days > 0 && productRentalPrices[productRentalPrices.length - 1].price > 0) {
            const productRentalPrice = newProductRentalPrice;
            productRentalPrice.days = productRentalPrices[productRentalPrices.length - 1].days + productRentalPrices[productRentalPrices.length - 1].days;
            setProductRentalPrices([...productRentalPrices, productRentalPrice]);
        }
    }, [productRentalPrices]);

    useEffect(() => {
        if (productPieces.length === 0 || productPieces[productPieces.length - 1].pieceName && productPieces[productPieces.length - 1].pieceName !== '') {
            setProductPieces([...productPieces, newProductPiece]);
        }
    }, [productPieces]);

    async function makeQuery() {
        if (id) {
            try {
                setLoading(true);
                const product = await getProductById(id);
                setFormData(product);
                setProductPieces(product.pieces)
                loadProductImages(product.id);

                const productRentalPrices = await getProductRentalPriceByProductId(id);
                setProductRentalPrices([...productRentalPrices, newProductRentalPrice]);

            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        } else if (!isCopying) {
            // Só reseta se não estiver no modo cópia
            setFormData(newEntity);
            setProductRentalPrices([newProductRentalPrice]);
            setProductPieces([newProductPiece]);
            setProductImages([]);
        }
    }

    useEffect(() => {
        if (!isCopying) {
            makeQuery();
        }
        setIsCopying(false);
    }, [id]);

    function validateInputs() {
        if (!formData.name || formData.name === '') {
            showAlert('Preencha o campo nome.');
            return false;
        }

        if(formData.costValue === undefined || formData.costValue === null || formData.costValue === '') {
            showAlert('Preencha o campo valor de compra.');
            return false;
        }

        if(formData.stock === undefined || formData.stock === null || formData.stock === '') {
            showAlert('Preencha o campo estoque.');
            return false;
        }

        return true;
    }

    async function saveEntity() {
        formData.pieces = productPieces.filter((p) => p.pieceName && p.pieceName !== '');
        const url = apiUrl + '/api/products';
        const productResponse = await makeRequest(url, formData.id ? 'PUT' : 'POST', {
            'Content-Type': 'application/json',
        }, JSON.stringify(formData));

        if (productResponse.id) {
            const currentProductRentalPrices = productRentalPrices.slice(0, -1);
            for (let i = 0; i < currentProductRentalPrices.length; i++) {
                const productRentalPrice = currentProductRentalPrices[i];
                productRentalPrice.product = productResponse;
                const url = apiUrl + '/api/product_rental_prices';
                await makeRequest(url, productRentalPrice.id ? 'PUT' : 'POST', {
                    'Content-Type': 'application/json',
                }, JSON.stringify(productRentalPrice));
            }
        }

        for (let i = 0; i < productRentalPricesToRemove.length; i++) {
            const productRentalPrice = productRentalPricesToRemove[i];
            if (productRentalPrice.id > 0) {
                const url = apiUrl + '/api/product_rental_prices/' + productRentalPrice.id;
                await makeRequest(url, 'DELETE', {
                    'Content-Type': 'application/json',
                }, JSON.stringify(productRentalPrice));
            }
        }
    }

    const onConfirm = async () => {
        try {
            if (validateInputs()) {
                setLoading(true);
                await new Promise(resolve => setTimeout(resolve, 300));
                await saveEntity();
                navigate('/app/productlist');
            }
        } catch (error) {
            console.error('Error saving:', error);
            showAlert('Error ao salvar.');
        } finally {
            setLoading(false);
        }
    };

    function clearEntity() {
        setFormData(newEntity);
        setProductRentalPrices([newProductRentalPrice]);
        setProductPieces([newProductPiece]);
    }

    function handleCopy() {
        // Ativar flag de cópia para evitar que o useEffect reset os dados
        setIsCopying(true);

        // Copiar dados do produto atual, excluindo id, code e availabilities
        // availabilities não devem ser copiadas pois são entidades managed pelo Hibernate
        // e causam erro "detached entity passed to persist" se incluídas
        const {id, code, availabilities, ...restFormData} = formData;
        const copiedFormData = {
            ...restFormData,
            name: formData.name ? `CÓPIA - ${formData.name}` : 'CÓPIA',
            availabilities: []  // Lista vazia para evitar erro de detached entity
        };
        setFormData(copiedFormData);

        // Copiar preços de locação, excluindo ids e filtrando apenas os válidos
        const copiedRentalPrices = productRentalPrices
            .filter((price) => price.days > 0 && price.price > 0)
            .map((price) => ({
                product: {id: 0, name: ''},
                days: price.days,
                price: price.price
            }));
        setProductRentalPrices([...copiedRentalPrices, newProductRentalPrice]);

        // Copiar peças, excluindo ids e filtrando apenas as válidas
        const copiedPieces = productPieces
            .filter((piece) => piece.pieceName && piece.pieceName !== '')
            .map((piece) => ({
                pieceName: piece.pieceName,
                quantity: piece.quantity
            }));
        setProductPieces([...copiedPieces, newProductPiece]);

        // Limpar listas de remoção
        setProductRentalPricesToRemove([]);
        setProductPiecesToRemove([]);

        // Remover id da URL para criar um novo produto
        const url = new URL(window.location.href);
        url.searchParams.delete('id');
        window.history.replaceState({}, '', url.toString());
        
        // Scroll para o topo
        window.scrollTo(0, 0);
    }

    function handleDaysChange(index, days) {
        setProductRentalPrices((prevItems) =>
            prevItems.map((item, i) =>
                i === index ? {
                    ...item,
                    days:
                    days
                } : item
            )
        );

    }

    function handlePieceNameChange(index, pieceName) {
        setProductPieces((prevItems) =>
            prevItems.map((item, i) =>
                i === index ? {
                    ...item,
                    pieceName:
                    pieceName
                } : item
            )
        );

    }

    function handlePieceQuantityChange(index, quantity) {
        setProductPieces((prevItems) =>
            prevItems.map((item, i) =>
                i === index ? {
                    ...item,
                    quantity:
                    quantity
                } : item
            )
        );

    }


    function handlePriceChange(index, price) {
        setProductRentalPrices((prevItems) =>
            prevItems.map((item, i) =>
                i === index ? {
                    ...item,
                    price:
                    price,
                } : item
            )
        );

    }

    const removeItem = (index) => {
        setProductRentalPricesToRemove([...productRentalPricesToRemove, productRentalPrices[index]]);
        const novaLista = [...productRentalPrices];
        novaLista.splice(index, 1);
        setProductRentalPrices(novaLista);
    };
    const removePiece = (index) => {
        const novaLista = [...productPieces];
        novaLista.splice(index, 1);
        setProductPieces(novaLista);
    };

    if (loadingPermissions) {
        return <Loading></Loading>;
    }
    if (hasUserPermission(UserPermission.CREATE_PRODUCT) === false || (hasUserPermission(UserPermission.EDIT_PRODUCT) === false && id)) {
        navigate('/unauthorizedaccess');
        return null;
    }

    return (
        <div className="app-form-page">

            <div style={{display: 'flex', alignItems: 'center'}}>
                <div className="header-label">{formData.id > 0 ? 'Editar' : 'Cadastrar'} Produto</div>
                {formData.id > 0 && (
                    <button onClick={handleCopy} style={{marginLeft: '10px', marginRight: '10px'}}>
                        Copiar
                    </button>
                )}
            </div>


            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Nome:</div>
                    <input id="productName" type="text" style={{width: '300px'}} value={formData.name} name="name"
                           onChange={handleChange}/>
                </div>

                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div>Estoque:</div>
                    <input type="number" style={{width: '100px'}} step="1" placeholder="1" value={formData.stock}
                           name="stock"
                           onChange={handleChange}/>
                </div>

                {/*<div style={{marginTop: '5px', alignItems: 'center'}}>*/}
                {/*    <div style={{width: '180px'}}>Estoque disponível:</div>*/}
                {/*    <input type="number" style={{width: '100px'}} step="0.01" placeholder="0.00"*/}
                {/*           value={formData.availableStock}*/}
                {/*           name="availableStock"*/}
                {/*           onChange={handleChange}/>*/}
                {/*</div>*/}

                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Valor de Compra:</div>
                    <DecimalInput
                        style={{width: '100px'}}
                        placeholder="0,00"
                        value={formData.costValue}
                        name="costValue"
                        onChange={handleChange}/>
                </div>

                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Preço de Venda:</div>
                    <DecimalInput
                        style={{width: '100px'}}
                        placeholder="0,00"
                        value={formData.value}
                        name="value"
                        onChange={handleChange}/>
                </div>

            </div>
            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div>Fornecedor:</div>
                    <input type="text" style={{width: '200px'}} value={formData.supplier} name="supplier"
                           onChange={handleChange}/>
                </div>

                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div>Marca:</div>
                    <input type="text" style={{width: '200px'}} value={formData.brand} name="brand"
                           onChange={handleChange}/>
                </div>

                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div>Idade Recomendada:</div>
                    <input type="text" style={{width: '150px'}} value={formData.recommendedAge} name="recommendedAge"
                           onChange={handleChange}/>
                </div>

                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div>Peso Máximo em KG:</div>
                    <input type="number" style={{width: '150px'}} step="1" placeholder="0"
                           value={formData.maxWeight}
                           name="maxWeight"
                           onChange={handleChange}/>
                </div>
            </div>

            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Detalhes:</div>
                    <textarea style={{width: '300px', height: '50px'}} value={formData.details} name="details"
                              onChange={handleChange}/>
                </div>

                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Orientações ao Cliente:</div>
                    <textarea style={{width: '300px', height: '50px'}} value={formData.customerOrientations}
                              name="customerOrientations"
                              onChange={handleChange}/>
                </div>
            </div>

            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '300px'}}>Palavras-chave (para busca por IA):</div>
                <textarea style={{width: '614px', height: '40px'}}
                          placeholder="ex: carrinho elétrico, jipe, carro de controle remoto"
                          value={formData.keywords || ''}
                          name="keywords"
                          onChange={handleChange}/>
            </div>


            <div style={{marginTop: '10px'}}>
                <div style={{marginBottom: '4px', fontWeight: 'bold'}}>Fotos do Produto <span style={{fontWeight:'normal', color:'#888', fontSize:'12px'}}>(máx. 2 MB por foto)</span></div>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-start'}}>
                    {productImages.map(img => (
                        <div key={img.id} style={{position: 'relative', display: 'inline-block'}}>
                            <img
                                src={`${apiUrl}/api/products/${formData.id}/images/${img.id}?auth=${encodeURIComponent(getAuthToken())}`}
                                alt="Foto do produto"
                                style={{width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd'}}
                            />
                            <button
                                onClick={() => handleImageDelete(img.id)}
                                style={{position:'absolute', top:'2px', right:'2px', background:'rgba(200,0,0,0.8)', color:'white', border:'none', borderRadius:'50%', width:'20px', height:'20px', cursor:'pointer', fontSize:'12px', lineHeight:'18px', padding:0}}
                                title="Remover foto"
                            >×</button>
                        </div>
                    ))}
                    <label style={{
                        width:'100px', height:'100px', display:'flex', flexDirection:'column',
                        alignItems:'center', justifyContent:'center', border:'2px dashed #aaa',
                        borderRadius:'4px', cursor: imageUploading ? 'wait' : 'pointer',
                        color:'#888', fontSize:'12px', textAlign:'center'
                    }}>
                        {imageUploading ? 'Enviando...' : '+ Adicionar\nFoto'}
                        <input type="file" accept="image/*" style={{display:'none'}} onChange={handleImageUpload} disabled={imageUploading} />
                    </label>
                </div>
            </div>

            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '250px'}}>Preços por período de Locação:</div>


                <table style={{height:'125px', border: '1px solid', borderColor: 'lightgray'}}>
                    <thead>
                    <tr>
                        <th>Dias Locação</th>
                        <th>Preço (R$)</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody style={{overflowY: 'auto'}}>
                    {productRentalPrices.map((item, index) => (
                        <tr key={item.id}>
                            <td>
                                <input key={item.id} type="number" step="1" placeholder="0"
                                       value={item.days}
                                       data-test={`rental-days-${index}`}
                                       onChange={(e) => handleDaysChange(index, parseFloat(e.target.value))}/>
                            </td>
                            <td>
                                <DecimalInput
                                    key={item.id}
                                    placeholder="0,00"
                                    value={item.price}
                                    data-test={`rental-price-${index}`}
                                    onChange={(e) => handlePriceChange(index, e.target.value)}/>
                            </td>
                            <td>
                                {index < productRentalPrices.length - 1 && (
                                    <DeleteButton onClick={() => removeItem(index)}/>)}
                            </td>
                        </tr>
                    ))}


                    </tbody>
                </table>
            </div>
            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>Peças:</div>

                <table style={{height: '125px', border: '1px solid', borderColor: 'lightgray'}}>
                    <thead>
                    <tr>
                        <th>Nome da Peça</th>
                        <th>Quantidade</th>
                        <th/>
                    </tr>
                    </thead>
                    <tbody style={{overflowY: 'auto'}}>
                    {productPieces.map((item, index) => (
                        <tr>
                            <td>
                                <input type="text"
                                       value={item.pieceName}
                                       data-test={`piece-name-${index}`}
                                       onChange={(e) => handlePieceNameChange(index, e.target.value)}/>
                            </td>
                            <td>
                                <input key={item.id} type="number" step="1" placeholder="1"
                                       value={item.quantity}
                                       data-test={`piece-quantity-${index}`}
                                       onChange={(e) => handlePieceQuantityChange(index, parseInt(e.target.value))}/>
                            </td>
                            <td>
                                {index < productPieces.length - 1 && (
                                    <DeleteButton className="remove-button" onClick={() => removePiece(index)}/>)}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            <ActionButtonsManagerView>
                <ConfirmButton onClick={onConfirm}/>
            </ActionButtonsManagerView>


        </div>
    );
};

export default ProductManager;
